import { useState } from 'react'
import { Button, Input, message, Segmented, Select, Space, Table, Tag, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { cronAnalyze, cronGenerate, timestampConvert } from '@/api/payloadDebug'
import type { CronResult, TimezoneName, TimestampResult, ZoneTimeItem } from '@/types'

const { Text } = Typography

const MONO = { fontFamily: "'JetBrains Mono', 'Consolas', monospace", fontSize: 12 }

const ZONE_OPTIONS: { value: TimezoneName; label: string }[] = [
  { value: 'Asia/Shanghai', label: '上海 Asia/Shanghai (UTC+8)' },
  { value: 'UTC', label: 'UTC (UTC+0)' },
  { value: 'Asia/Tokyo', label: '东京 Asia/Tokyo (UTC+9)' },
  { value: 'Asia/Singapore', label: '新加坡 Asia/Singapore (UTC+8)' },
  { value: 'America/New_York', label: '纽约 America/New_York (UTC-5)' },
  { value: 'America/Los_Angeles', label: '洛杉矶 America/Los_Angeles (UTC-8)' },
  { value: 'Europe/London', label: '伦敦 Europe/London (UTC+0)' },
  { value: 'Europe/Berlin', label: '柏林 Europe/Berlin (UTC+1)' },
]

/** 当前时间戳（秒） */
const nowTs = () => String(Math.floor(Date.now() / 1000))

/** 当前本地时间（YYYY-MM-DD HH:mm:ss） */
const nowLocal = () => {
  const d = new Date()
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`
}

/** 时间戳与 cron 页签：互转、时区、cron 表达式生成与解析 */
export default function TimeCronTab() {
  // 时间戳互转
  const [tsMode, setTsMode] = useState<'ts_to_date' | 'date_to_ts'>('ts_to_date')
  const [tsValue, setTsValue] = useState(nowTs)
  const [zone, setZone] = useState<TimezoneName>('Asia/Shanghai')
  const [tsResult, setTsResult] = useState<TimestampResult | null>(null)

  // cron
  const [cronDesc, setCronDesc] = useState('')
  const [cronExpr, setCronExpr] = useState('0 9 * * 1-5')
  const [cronResult, setCronResult] = useState<CronResult | null>(null)
  const [generating, setGenerating] = useState(false)

  /** 时间戳/日期互转 */
  const handleTsConvert = async () => {
    if (!tsValue.trim()) {
      message.warning('请输入时间戳或日期文本')
      return
    }
    try {
      const res = await timestampConvert({ action: tsMode, value: tsValue, zone })
      setTsResult(res)
    } finally {
      /* 拦截器已提示错误 */
    }
  }

  /** cron 文字描述生成（生成成功后自动填充表达式并展示解析结果） */
  const handleCronGenerate = async () => {
    if (!cronDesc.trim()) {
      message.warning('请输入文字描述，如：每天凌晨1点执行')
      return
    }
    setGenerating(true)
    try {
      const res = await cronGenerate({ text: cronDesc })
      setCronResult(res)
      if (res.valid) {
        setCronExpr(res.expr)
        message.success(`已生成表达式：${res.expr}`)
      }
    } finally {
      setGenerating(false)
    }
  }

  /** cron 解析 */
  const handleCron = async () => {
    if (!cronExpr.trim()) {
      message.warning('请输入 cron 表达式')
      return
    }
    const res = await cronAnalyze({ expr: cronExpr, next_count: 5 })
    setCronResult(res)
  }

  const zoneColumns: ColumnsType<ZoneTimeItem> = [
    { title: '时区', dataIndex: 'zone', width: 200 },
    { title: '当地时间', dataIndex: 'time', render: (v: string) => <span style={MONO}>{v}</span> },
    { title: '星期', dataIndex: 'weekday', width: 60, align: 'center' },
    { title: 'UTC 偏移', dataIndex: 'offset', width: 90, render: (v: string) => <span style={MONO}>{v}</span> },
    { title: 'ISO 8601', dataIndex: 'iso', ellipsis: true, render: (v: string) => <span style={MONO}>{v}</span> },
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 14, padding: '0 22px 12px', overflowY: 'auto' }}>
      {/* ===== 时间戳互转 ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>时间戳互转</Text>
        <Space wrap style={{ marginBottom: 10 }}>
          <Segmented
            value={tsMode}
            onChange={(v) => {
              const next = v as 'ts_to_date' | 'date_to_ts'
              setTsMode(next)
              setTsValue(next === 'ts_to_date' ? nowTs() : nowLocal()) // 切换时默认当前时间，格式随模式变化
              setTsResult(null)
            }}
            options={[{ value: 'ts_to_date', label: '时间戳 → 日期' }, { value: 'date_to_ts', label: '日期 → 时间戳' }]}
          />
          <Input
            placeholder={tsMode === 'ts_to_date' ? '时间戳（秒/毫秒自动识别）' : 'YYYY-MM-DD[ HH:MM:SS]'}
            value={tsValue}
            onChange={(e) => setTsValue(e.target.value)}
            style={{ width: 280, ...MONO }}
          />
          {tsMode === 'date_to_ts' && (
            <Select value={zone} onChange={setZone} options={ZONE_OPTIONS} style={{ width: 240 }} />
          )}
          <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} onClick={handleTsConvert}>
            转换
          </Button>
        </Space>
        {tsResult && (
          tsMode === 'ts_to_date' ? (
            <>
              <Space style={{ marginBottom: 6 }}>
                <Text type="secondary">输入 {tsResult.input}</Text>
                <Tag color="blue">已识别为 {tsResult.unit === 'ms' ? '毫秒' : '秒'}</Tag>
              </Space>
              <Table
                size="small"
                columns={zoneColumns}
                dataSource={tsResult.zones ?? []}
                rowKey="zone"
                pagination={false}
              />
            </>
          ) : (
            <Space wrap>
              <Tag color="blue">秒 {tsResult.ts_s}</Tag>
              <Tag color="blue">毫秒 {tsResult.ts_ms}</Tag>
              <Text type="secondary">对应 UTC：{tsResult.utc}</Text>
            </Space>
          )
        )}
      </div>

      {/* ===== cron 表达式 ===== */}
      <div style={{ flexShrink: 0 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>cron 表达式（分 时 日 月 周）</Text>
        <Space wrap style={{ marginBottom: 10 }}>
          <Input
            placeholder="文字描述生成，如：每天凌晨1点执行 / 每周一三五七执行"
            value={cronDesc}
            onChange={(e) => setCronDesc(e.target.value)}
            onPressEnter={handleCronGenerate}
            style={{ width: 340 }}
          />
          <Button loading={generating} onClick={handleCronGenerate}>生成</Button>
        </Space>
        <div style={{ marginBottom: 10 }}>
          <Space wrap>
            <Input
              placeholder="*/5 * * * *"
              value={cronExpr}
              onChange={(e) => setCronExpr(e.target.value)}
              onPressEnter={handleCron}
              style={{ width: 220, ...MONO }}
            />
            <Button type="primary" style={{ background: 'var(--au-primary)', border: 'none' }} onClick={handleCron}>
              解析
            </Button>
          </Space>
        </div>
        {cronResult && !cronResult.valid && <Tag color="error">{cronResult.error}</Tag>}
        {cronResult?.valid && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {cronResult.matched && (
              <Space wrap>
                <Text type="secondary">识别到：</Text>
                {cronResult.matched.map((mk) => (
                  <Tag key={mk} color="cyan">{mk}</Tag>
                ))}
              </Space>
            )}
            <Space wrap>
              <Tag color="purple">{cronResult.describe}</Tag>
              {(cronResult.fields ?? []).map((f) => (
                <Tag key={f.name} color="default">{f.name}：{f.value}（{f.desc}）</Tag>
              ))}
            </Space>
            <div>
              <Text type="secondary">未来 {cronResult.next_runs?.length} 次执行：</Text>
              {cronResult.next_runs?.map((t) => (
                <Tag key={t} style={MONO}>{t}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
